import React, { useState, useEffect, useMemo, useCallback } from "react";
import ReactQuill from "react-quill-new";
import DOMPurify from "dompurify";
import "react-quill-new/dist/quill.snow.css";
import { Contact, getAvatarColor, EmailTemplate } from "./types";
import GoogleAvatar from "./GoogleAvatar";
import { formatFileSize, getFileTypeIcon } from "../utils/fileUtils";
import TemplateSelector from "./templates/TemplateSelector";
import CustomTemplateUpload from "./templates/CustomTemplateUpload";
import EmailPreview from "./EmailPreview";

interface BulkEmailModalProps {
  contacts: Contact[];
  onClose: () => void;
  onSend: (
    subject: string,
    htmlBody: string,
    recipients: Contact[],
    attachments: File[],
    draftId?: string,
    templateId?: string,
    templateName?: string
  ) => Promise<void>;
  isSending: boolean;
  senderEmail: string;
  senderName?: string;
  senderPhoto?: string;
  onSuccess?: () => void; // Called after successful send to clear selection
  accessToken: string;
  draftData?: {
    draftId: string;
    subject: string;
    htmlBody: string;
  };
  onDraftSaved?: () => void;
}

type ModalStep = "compose" | "preview";

const BulkEmailModal: React.FC<BulkEmailModalProps> = ({
  contacts,
  onClose,
  onSend,
  isSending,
  senderEmail,
  senderName,
  senderPhoto,
  onSuccess,
  accessToken,
  draftData,
  onDraftSaved,
}) => {
  const [step, setStep] = useState<ModalStep>("compose");
  const [subject, setSubject] = useState("");
  const [htmlBody, setHtmlBody] = useState("");
  const [recipientsExpanded, setRecipientsExpanded] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [signature, setSignature] = useState("");
  const [signatureLoaded, setSignatureLoaded] = useState(false);
  const [originalSignature, setOriginalSignature] = useState("");
  const [isSavingSignature, setIsSavingSignature] = useState(false);
  const [signatureSaveSuccess, setSignatureSaveSuccess] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [draftSaveError, setDraftSaveError] = useState<string | null>(null);
  const [draftSaveSuccess, setDraftSaveSuccess] = useState(false);
  const [lastSavedDraftContent, setLastSavedDraftContent] = useState<{
    subject: string;
    htmlBody: string;
  } | null>(null);

  // Template state
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [showCustomTemplateUpload, setShowCustomTemplateUpload] = useState(false);
  const [templateRefreshKey, setTemplateRefreshKey] = useState(0);

  // File attachment constraints
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const MAX_ATTACHMENTS = 5;
  const MAX_TOTAL_SIZE = 25 * 1024 * 1024; // 25MB
  const ALLOWED_TYPES = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "image/jpeg",
    "image/png",
    "image/gif",
    "text/plain",
    "application/zip",
  ];

  // Check if user has entered any content or added attachments
  const hasContent = subject.trim() || htmlBody.trim() || attachments.length > 0;

  // Check if changes have been made since last draft save
  const hasUnsavedChanges =
    hasContent &&
    (!lastSavedDraftContent ||
      subject !== lastSavedDraftContent.subject ||
      htmlBody !== lastSavedDraftContent.htmlBody ||
      attachments.length > 0);

  // Handle close with confirmation if content exists
  const handleClose = useCallback(() => {
    if (hasUnsavedChanges && !isSending) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, isSending, onClose]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isSending) {
        handleClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [handleClose, isSending]);

  // Load user's signature on mount
  useEffect(() => {
    const loadSignature = async () => {
      try {
        const response = await fetch("/api/emails/signature", {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          const loadedSignature = data.signature_text || "";
          setSignature(loadedSignature);
          setOriginalSignature(loadedSignature);
        }
      } catch (err) {
        console.error("Failed to load signature:", err);
      } finally {
        setSignatureLoaded(true);
      }
    };

    loadSignature();
  }, [accessToken]);

  // Handle signature save
  const handleSaveSignature = async () => {
    // Validate signature size (Azure Table Storage has 64KB entity limit)
    // Allow 50KB for signature to leave room for other entity fields
    const MAX_SIGNATURE_SIZE = 50 * 1024; // 50KB
    const signatureSize = new Blob([signature]).size;

    if (signatureSize > MAX_SIGNATURE_SIZE) {
      alert(
        `Signature is too large (${(signatureSize / 1024).toFixed(1)}KB). Maximum size is 50KB. Please reduce the amount of formatting or content.`
      );
      return;
    }

    setIsSavingSignature(true);
    setSignatureSaveSuccess(false);
    try {
      const response = await fetch("/api/emails/signature", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ signature_text: signature }),
      });

      if (response.ok) {
        setOriginalSignature(signature);
        setSignatureSaveSuccess(true);
        setTimeout(() => setSignatureSaveSuccess(false), 3000);
      } else {
        const error = await response.json();
        throw new Error(error.error || "Failed to save signature");
      }
    } catch (err) {
      console.error("Failed to save signature:", err);
      alert(err instanceof Error ? err.message : "Failed to save signature");
    } finally {
      setIsSavingSignature(false);
    }
  };

  // Check if signature has been modified
  const isSignatureModified = signatureLoaded && signature !== originalSignature;

  // Load draft data if provided
  useEffect(() => {
    if (draftData) {
      setDraftId(draftData.draftId);
      setSubject(draftData.subject);
      setHtmlBody(draftData.htmlBody);
      // Mark this as the last saved draft content
      setLastSavedDraftContent({
        subject: draftData.subject,
        htmlBody: draftData.htmlBody,
      });
    }
  }, [draftData]);

  // Handle draft save
  const handleSaveDraft = async () => {
    setIsSavingDraft(true);
    setDraftSaveError(null);
    setDraftSaveSuccess(false);

    try {
      // Use FormData to support file uploads
      const formData = new FormData();

      // Add draft data as JSON string
      formData.append(
        "draftData",
        JSON.stringify({
          draftId: draftId || undefined,
          subject,
          htmlBody,
          recipients: contacts.map((c) => ({
            contact_id: c.contact_id,
            email: c.email,
            name: c.full_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || "",
          })),
        })
      );

      // Add attachments as files
      attachments.forEach((file, index) => {
        formData.append(`attachment_${index}`, file);
      });

      const response = await fetch("/api/emails/drafts", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          // Don't set Content-Type - browser will set it with boundary for multipart/form-data
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to save draft");
      }

      const data = await response.json();
      setDraftId(data.draftId);
      setDraftSaveSuccess(true);
      setTimeout(() => setDraftSaveSuccess(false), 3000);

      // Save the current content as the last saved draft
      setLastSavedDraftContent({
        subject,
        htmlBody,
      });

      if (onDraftSaved) {
        onDraftSaved();
      }
    } catch (err) {
      console.error("Failed to save draft:", err);
      setDraftSaveError(err instanceof Error ? err.message : "Failed to save draft");
    } finally {
      setIsSavingDraft(false);
    }
  };

  // Quill editor modules configuration - email-safe subset
  const quillModules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ align: [] }],
        ["link"],
        ["clean"],
      ],
    }),
    []
  );

  const quillFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "list",
    "align",
    "link",
  ];

  const canProceedToPreview = subject.trim() && htmlBody.trim();

  // File handling functions
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    // Validate file count
    if (attachments.length + files.length > MAX_ATTACHMENTS) {
      setAttachmentError(`Maximum ${MAX_ATTACHMENTS} attachments allowed`);
      return;
    }

    // Validate file types and sizes
    for (const file of files) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        setAttachmentError(`${file.name}: File type not allowed`);
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        setAttachmentError(`${file.name}: File too large (max 10MB)`);
        return;
      }
    }

    // Validate total size
    const totalSize = [...attachments, ...files].reduce((sum, f) => sum + f.size, 0);
    if (totalSize > MAX_TOTAL_SIZE) {
      setAttachmentError("Total attachment size exceeds 25MB");
      return;
    }

    setAttachments((prev) => [...prev, ...files]);
    setAttachmentError(null);
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
    setAttachmentError(null);
  };

  // File size formatting moved to shared utility (utils/fileUtils.ts)

  const handleSend = async () => {
    let finalHtmlBody: string;

    if (selectedTemplate) {
      // Apply template - replace markers with content
      // Note: Personalization ({{first_name}} etc.) is handled per-recipient on the server
      finalHtmlBody = selectedTemplate.html_content
        .replace(/\{\{content\}\}/gi, htmlBody)
        .replace(/\{\{subject\}\}/gi, subject)
        .replace(/\{\{signature\}\}/gi, signature || "");
    } else {
      // No template - just append signature if present
      const cleanedBody = cleanQuillHtml(htmlBody);
      finalHtmlBody = signature.trim()
        ? `${cleanedBody}<br/><br/><div style="color: #666; font-size: 13px; border-top: 1px solid #ddd; padding-top: 10px; margin-top: 20px;">${signature}</div>`
        : cleanedBody;
    }

    await onSend(
      subject,
      finalHtmlBody,
      contacts,
      attachments,
      draftId || undefined,
      selectedTemplate?.template_id,
      selectedTemplate?.name
    );
    // Call onSuccess callback to clear selection in parent
    onSuccess?.();
  };

  // Get initials for avatar
  const getInitials = (contact: Contact): string => {
    const first = contact.first_name?.[0] || "";
    const last = contact.last_name?.[0] || "";
    return (first + last).toUpperCase() || "?";
  };

  // Insert placeholder at cursor position
  const insertPlaceholder = (placeholder: string) => {
    setHtmlBody((prev) => prev + placeholder);
  };

  // Clean up Quill HTML formatting issues
  const cleanQuillHtml = (html: string): string => {
    let cleaned = html;

    // Remove empty paragraphs at start and end
    cleaned = cleaned.replace(/^(<p><br><\/p>|<p>\s*<\/p>)+/g, "");
    cleaned = cleaned.replace(/(<p><br><\/p>|<p>\s*<\/p>)+$/g, "");

    // Convert <p><br></p> to <p>&nbsp;</p> for better spacing consistency
    cleaned = cleaned.replace(/<p><br><\/p>/g, "<p>&nbsp;</p>");

    // Normalize multiple consecutive empty paragraphs to max 2
    cleaned = cleaned.replace(/(<p>&nbsp;<\/p>){3,}/g, "<p>&nbsp;</p><p>&nbsp;</p>");

    // Trim excessive whitespace in paragraphs
    cleaned = cleaned.replace(/<p>\s+/g, "<p>");
    cleaned = cleaned.replace(/\s+<\/p>/g, "</p>");

    return cleaned;
  };

  // Sanitize HTML for preview
  const sanitizedHtmlBody = useMemo(() => {
    const cleaned = cleanQuillHtml(htmlBody);
    return DOMPurify.sanitize(cleaned, {
      ALLOWED_TAGS: [
        "p",
        "br",
        "strong",
        "b",
        "em",
        "i",
        "u",
        "s",
        "strike",
        "a",
        "ul",
        "ol",
        "li",
        "h1",
        "h2",
        "h3",
        "span",
        "div",
      ],
      ALLOWED_ATTR: ["href", "target", "style", "class"],
    });
  }, [htmlBody]);

  // Preview subject with example personalization
  const previewSubject = useMemo(() => {
    const exampleContact = contacts[0];
    if (!exampleContact) return subject;
    return subject
      .replace(
        /\{\{first_name\}\}/gi,
        exampleContact.first_name || exampleContact.full_name?.split(" ")[0] || "John"
      )
      .replace(/\{\{last_name\}\}/gi, exampleContact.last_name || "")
      .replace(/\{\{full_name\}\}/gi, exampleContact.full_name || "John Doe");
  }, [subject, contacts]);

  // Helper function to apply template to content
  const applyTemplate = useCallback(
    (content: string, subjectText: string, signatureText: string, contact?: Contact) => {
      if (!selectedTemplate) {
        // No template - just add signature if present
        if (signatureText.trim()) {
          return `${content}<br/><br/><div style="color: #666; font-size: 13px; border-top: 1px solid #ddd; padding-top: 10px; margin-top: 20px;">${signatureText}</div>`;
        }
        return content;
      }

      // Apply template
      let templatedHtml = selectedTemplate.html_content
        .replace(/\{\{content\}\}/gi, content)
        .replace(/\{\{subject\}\}/gi, subjectText)
        .replace(/\{\{signature\}\}/gi, signatureText || "");

      // Apply personalization if contact provided
      if (contact) {
        templatedHtml = templatedHtml
          .replace(
            /\{\{first_name\}\}/gi,
            contact.first_name || contact.full_name?.split(" ")[0] || "John"
          )
          .replace(/\{\{last_name\}\}/gi, contact.last_name || "")
          .replace(/\{\{full_name\}\}/gi, contact.full_name || "John Doe");
      }

      return templatedHtml;
    },
    [selectedTemplate]
  );

  // Preview HTML with example personalization, signature, and template
  const previewHtml = useMemo(() => {
    const exampleContact = contacts[0];

    // First personalize the body content
    const personalizedBody = !exampleContact
      ? sanitizedHtmlBody
      : sanitizedHtmlBody
          .replace(
            /\{\{first_name\}\}/gi,
            exampleContact.first_name || exampleContact.full_name?.split(" ")[0] || "John"
          )
          .replace(/\{\{last_name\}\}/gi, exampleContact.last_name || "")
          .replace(/\{\{full_name\}\}/gi, exampleContact.full_name || "John Doe");

    // Apply template (or just add signature if no template)
    return applyTemplate(personalizedBody, previewSubject, signature, exampleContact);
  }, [sanitizedHtmlBody, contacts, signature, previewSubject, applyTemplate]);

  // Check if using a template (for UI display)
  const isUsingTemplate = selectedTemplate !== null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <div className="bg-navy rounded border border-border-strong max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-border-strong bg-gradient-to-r from-navy to-navy/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    {step === "compose" ? "Compose Email" : "Preview & Send"}
                  </h3>
                  <p className="text-sm text-text-secondary">
                    {contacts.length} recipient{contacts.length !== 1 ? "s" : ""}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                disabled={isSending}
                className="p-2 rounded-sm text-text-secondary hover:text-white hover:bg-navy-mid transition-colors disabled:opacity-50"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Step indicator */}
            <div className="flex items-center gap-2 mt-4">
              <div
                className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm ${
                  step === "compose" ? "bg-rose-dim text-rose-accent" : "text-text-muted"
                }`}
              >
                <span className="w-5 h-5 rounded-full bg-rose-dim flex items-center justify-center text-xs">
                  1
                </span>
                Compose
              </div>
              <svg
                className="w-4 h-4 text-navy-light"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
              <div
                className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm ${
                  step === "preview" ? "bg-rose-dim text-rose-accent" : "text-text-muted"
                }`}
              >
                <span className="w-5 h-5 rounded-full bg-rose-dim flex items-center justify-center text-xs">
                  2
                </span>
                Preview & Send
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {step === "compose" ? (
              <div className="p-6 space-y-4">
                {/* Template Selector */}
                <TemplateSelector
                  key={templateRefreshKey}
                  selectedTemplateId={selectedTemplate?.template_id || null}
                  onSelectTemplate={setSelectedTemplate}
                  accessToken={accessToken}
                  onAddCustomTemplate={() => setShowCustomTemplateUpload(true)}
                />

                {/* Custom Template Upload (inline, collapsible) */}
                {showCustomTemplateUpload && (
                  <CustomTemplateUpload
                    accessToken={accessToken}
                    onClose={() => setShowCustomTemplateUpload(false)}
                    onTemplateSaved={(template) => {
                      setSelectedTemplate(template);
                      setShowCustomTemplateUpload(false);
                      // Trigger refresh of template list to include the new template
                      setTemplateRefreshKey((prev) => prev + 1);
                    }}
                  />
                )}

                {/* Subject with preview */}
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">Subject</label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Enter email subject..."
                    className="w-full px-4 py-3 rounded bg-navy-mid/50 border border-border-strong text-white placeholder-text-muted focus:outline-none focus:border-rose-accent/40 focus:ring-2 focus:ring-rose-accent/15 transition-all"
                  />
                  {/* Subject Preview */}
                  {subject.includes("{{") && (
                    <div className="mt-2 px-3 py-2 bg-navy-mid/30 rounded-sm border border-border-subtle">
                      <p className="text-xs text-text-muted mb-1">Preview (first recipient):</p>
                      <p className="text-sm text-text-primary">{previewSubject}</p>
                    </div>
                  )}
                </div>

                {/* Personalization Placeholders */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-text-muted">Add personalization:</span>
                  <button
                    type="button"
                    onClick={() => insertPlaceholder("{{first_name}}")}
                    className="px-2 py-1 text-xs bg-rose-dim text-rose-accent rounded hover:bg-rose-dim transition-colors"
                  >
                    {"{{first_name}}"}
                  </button>
                  <button
                    type="button"
                    onClick={() => insertPlaceholder("{{last_name}}")}
                    className="px-2 py-1 text-xs bg-rose-dim text-rose-accent rounded hover:bg-rose-dim transition-colors"
                  >
                    {"{{last_name}}"}
                  </button>
                  <button
                    type="button"
                    onClick={() => insertPlaceholder("{{full_name}}")}
                    className="px-2 py-1 text-xs bg-rose-dim text-rose-accent rounded hover:bg-rose-dim transition-colors"
                  >
                    {"{{full_name}}"}
                  </button>
                  <span className="text-xs text-navy-light ml-2">
                    - Use these to personalize each email
                  </span>
                </div>

                {/* Email Body Editor */}
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Email Body
                  </label>
                  <div className="quill-dark rounded overflow-hidden border border-border-strong">
                    <ReactQuill
                      theme="snow"
                      value={htmlBody}
                      onChange={setHtmlBody}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="Compose your email... Try starting with 'Hi {{first_name}},' for a personal touch!"
                    />
                  </div>
                  <p className="text-xs text-text-muted mt-2">
                    Use the toolbar above to format your email. The editor supports email-safe HTML.
                  </p>
                </div>

                {/* Email Signature */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-text-primary">
                      Email Signature
                    </label>
                    {isSignatureModified && (
                      <button
                        onClick={handleSaveSignature}
                        disabled={isSavingSignature}
                        className="px-3 py-1 text-xs font-medium text-white bg-navy-mid hover:bg-navy-light disabled:bg-navy-deep disabled:cursor-not-allowed rounded-sm transition-colors flex items-center gap-1.5"
                      >
                        {isSavingSignature ? (
                          <>
                            <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Saving...
                          </>
                        ) : signatureSaveSuccess ? (
                          <>
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                            Saved!
                          </>
                        ) : (
                          <>
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                              />
                            </svg>
                            Save Permanently
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  <div
                    className={`quill-dark signature-editor rounded overflow-hidden border border-border-strong ${!signatureLoaded ? "opacity-60" : ""}`}
                  >
                    <ReactQuill
                      theme="snow"
                      value={signature}
                      onChange={setSignature}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder={
                        signatureLoaded
                          ? "Your signature (name, title, contact info)"
                          : "Loading..."
                      }
                      className="bg-navy-mid/50"
                      style={{ minHeight: "70px" }}
                      readOnly={!signatureLoaded}
                    />
                  </div>
                  <p className="text-xs text-text-muted mt-2">
                    {isSignatureModified ? (
                      <span className="text-amber-400">
                        Signature modified - click &quot;Save Permanently&quot; to save for future
                        emails
                      </span>
                    ) : (
                      "This signature will be automatically added to the bottom of your emails."
                    )}
                  </p>
                </div>

                {/* File Attachments */}
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Attachments (Optional)
                  </label>

                  <input
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt,.zip"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="attachment-input"
                    disabled={attachments.length >= MAX_ATTACHMENTS}
                  />

                  <label
                    htmlFor="attachment-input"
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-border-strong text-text-primary hover:bg-navy-mid/50 transition-colors cursor-pointer ${
                      attachments.length >= MAX_ATTACHMENTS ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                      />
                    </svg>
                    Add Files ({attachments.length}/{MAX_ATTACHMENTS})
                  </label>

                  {attachmentError && (
                    <p className="text-xs text-red-400 mt-2">{attachmentError}</p>
                  )}

                  {attachments.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {attachments.map((file, index) => {
                        const fileIcon = getFileTypeIcon(file.type);
                        return (
                          <div
                            key={index}
                            className="flex items-center gap-3 px-3 py-2 bg-navy-mid/30 rounded-sm"
                          >
                            <svg
                              className="w-5 h-5 flex-shrink-0"
                              viewBox={fileIcon.viewBox}
                              fill={fileIcon.color}
                            >
                              <path d={fileIcon.path} />
                            </svg>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-text-primary truncate">{file.name}</p>
                              <p className="text-xs text-text-muted">{formatFileSize(file.size)}</p>
                            </div>
                            <button
                              onClick={() => removeAttachment(index)}
                              className="p-1 rounded hover:bg-navy-light transition-colors"
                            >
                              <svg
                                className="w-4 h-4 text-text-secondary"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <p className="text-xs text-text-muted mt-2">
                    Max 10MB per file. Supported: PDF, Word, Excel, Images, Text, ZIP
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-6 space-y-4">
                {/* Recipients List (Collapsible) */}
                <div className="rounded border border-border-strong overflow-hidden">
                  <button
                    onClick={() => setRecipientsExpanded(!recipientsExpanded)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-navy-mid/30 hover:bg-navy-mid/50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <svg
                        className="w-4 h-4 text-text-secondary"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                      </svg>
                      <span className="text-sm text-text-primary">
                        {contacts.length} Recipient{contacts.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <svg
                      className={`w-4 h-4 text-text-muted transition-transform ${recipientsExpanded ? "rotate-180" : ""}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {recipientsExpanded && (
                    <div className="max-h-48 overflow-y-auto border-t border-border-strong">
                      {contacts.map((contact) => {
                        const initials = getInitials(contact);
                        const avatarColor = getAvatarColor(contact.full_name);
                        return (
                          <div
                            key={contact.contact_id}
                            className="flex items-center gap-3 px-4 py-2.5 border-b border-border-subtle last:border-0"
                          >
                            <div
                              className={`flex-shrink-0 w-8 h-8 rounded-sm bg-gradient-to-br ${avatarColor} flex items-center justify-center text-white text-xs font-medium`}
                            >
                              {initials}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white truncate">
                                {contact.full_name || contact.email}
                              </p>
                              <p className="text-xs text-text-muted truncate">{contact.email}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Email Preview */}
                <div>
                  <p className="text-xs text-text-secondary mb-2">
                    Preview for {contacts[0]?.full_name || contacts[0]?.email || "first recipient"}
                    {contacts.length > 1 &&
                      " — each recipient will receive their own personalized email"}
                  </p>

                  <EmailPreview
                    htmlContent={previewHtml}
                    subject={previewSubject}
                    senderEmail={senderEmail}
                    senderName={senderName}
                    senderPhoto={senderPhoto}
                    recipientEmail={contacts[0]?.email || "recipient@example.com"}
                    isTemplate={isUsingTemplate}
                    templateName={isUsingTemplate ? selectedTemplate?.name : undefined}
                    bodyMaxHeight="400px"
                  />
                  {isUsingTemplate && (
                    <p className="text-xs text-text-muted mt-2 text-center">
                      Preview may vary slightly across email clients
                    </p>
                  )}

                  {/* Attachments Preview (shown for both template and non-template) */}
                  {attachments.length > 0 && (
                    <div className="mt-3 p-4 rounded bg-navy-mid/30 border border-border-subtle">
                      <p className="text-xs font-medium text-text-primary mb-2 flex items-center gap-1.5">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                          />
                        </svg>
                        {attachments.length} Attachment{attachments.length !== 1 ? "s" : ""}
                      </p>
                      <div className="space-y-1.5">
                        {attachments.map((file, index) => {
                          const fileIcon = getFileTypeIcon(file.type);
                          return (
                            <div
                              key={index}
                              className="flex items-center justify-between p-2 rounded-sm bg-navy/50 border border-border-subtle"
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <svg
                                  className="w-5 h-5 flex-shrink-0"
                                  viewBox={fileIcon.viewBox}
                                  fill={fileIcon.color}
                                >
                                  <path d={fileIcon.path} />
                                </svg>
                                <div className="min-w-0">
                                  <p className="text-sm text-text-primary truncate">{file.name}</p>
                                  <p className="text-xs text-text-muted">
                                    {formatFileSize(file.size)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Warning */}
                <div className="flex items-start gap-3 p-4 rounded bg-amber-500/10 border border-amber-500/30">
                  <svg
                    className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <div>
                    <p className="text-sm text-amber-300 font-medium">Ready to send?</p>
                    <p className="text-xs text-amber-400/70 mt-0.5">
                      This will send the email to {contacts.length} contact
                      {contacts.length !== 1 ? "s" : ""} from {senderEmail}.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Draft save error */}
          {draftSaveError && (
            <div className="px-6 py-3 bg-red-500/10 border-t border-red-500/30">
              <p className="text-sm text-red-400">{draftSaveError}</p>
            </div>
          )}

          {/* Footer */}
          <div className="px-6 py-4 border-t border-border-strong bg-navy/80 flex gap-3 justify-between">
            <div>
              {step === "preview" && (
                <button
                  onClick={() => setStep("compose")}
                  disabled={isSending}
                  className="px-5 py-2.5 rounded-sm bg-navy-mid text-text-primary hover:bg-navy-light transition-colors disabled:opacity-50 font-medium"
                >
                  Back to Edit
                </button>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleClose}
                disabled={isSending}
                className="px-5 py-2.5 rounded-sm bg-navy-mid text-text-primary hover:bg-navy-light transition-colors disabled:opacity-50 font-medium"
              >
                Cancel
              </button>
              {step === "compose" ? (
                <>
                  <button
                    onClick={handleSaveDraft}
                    disabled={isSavingDraft || (!subject.trim() && !htmlBody.trim())}
                    className="px-5 py-2.5 rounded-sm bg-navy-light text-white hover:bg-navy-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
                  >
                    {isSavingDraft ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Saving...
                      </>
                    ) : draftSaveSuccess ? (
                      <>
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        Saved!
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                          />
                        </svg>
                        Save Draft
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setStep("preview")}
                    disabled={!canProceedToPreview}
                    className="px-5 py-2.5 rounded-sm bg-navy-mid border border-rose-accent text-rose-accent hover:bg-rose-accent hover:text-navy-darkest transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium shadow-lg shadow-rose-accent/15"
                  >
                    Preview
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </>
              ) : (
                <button
                  onClick={handleSend}
                  disabled={isSending}
                  className="px-5 py-2.5 rounded-sm bg-emerald-600 text-white hover:bg-emerald-500 transition-colors disabled:opacity-50 flex items-center gap-2 font-medium shadow-lg shadow-emerald-500/20"
                >
                  {isSending ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                        />
                      </svg>
                      Send Email
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Custom styles for Quill editor */}
        <style>{`
          .ql-container {
            min-height: 180px;
            font-size: 14px;
          }
          .ql-editor {
            min-height: 180px;
          }
          .signature-editor .ql-container,
          .signature-editor .ql-editor {
            min-height: 120px;
          }
          .ql-toolbar.ql-snow {
            border: none;
            border-bottom: 1px solid #e5e7eb;
            background: #f9fafb;
          }
          .ql-container.ql-snow {
            border: none;
          }
        `}</style>
      </div>

      {/* Close Confirmation Dialog */}
      {showCloseConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-navy rounded border border-border-strong max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded bg-amber-500/20 flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-amber-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">
                  {lastSavedDraftContent ? "Unsaved changes" : "Discard draft?"}
                </h3>
                <p className="text-sm text-text-secondary">
                  {lastSavedDraftContent
                    ? "You have unsaved changes. Save your draft before closing to keep your changes."
                    : "Your email content will be lost."}
                </p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              {lastSavedDraftContent && (
                <button
                  onClick={async () => {
                    await handleSaveDraft();
                    setShowCloseConfirm(false);
                    onClose();
                  }}
                  className="px-4 py-2 rounded-sm bg-navy-mid border border-rose-accent text-rose-accent hover:bg-rose-accent hover:text-navy-darkest transition-colors font-medium"
                >
                  Save & Close
                </button>
              )}
              <button
                onClick={() => setShowCloseConfirm(false)}
                className="px-4 py-2 rounded-sm bg-navy-mid text-text-primary hover:bg-navy-light transition-colors font-medium"
              >
                Keep Editing
              </button>
              <button
                onClick={() => {
                  setShowCloseConfirm(false);
                  onClose();
                }}
                className="px-4 py-2 rounded-sm bg-red-600 text-white hover:bg-red-500 transition-colors font-medium"
              >
                {lastSavedDraftContent ? "Discard Changes" : "Discard"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BulkEmailModal;
